import { Disclosure, Transition } from "@headlessui/react";
import clsx from "clsx";
import {
  Fragment,
  MouseEvent,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
} from "react";

import { ChevronDown } from "@/assets/icons/ds";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { useIsMobile } from "@/hooks/useIsMobile";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

type CollapsibleProps = {
  id: string;
  label: string;
  children: ReactNode;
};

/**
 * Accordion-style collapsible group used in the footer: a disclosure button
 * (aria-expanded/aria-controls) that shows a list of links or buttons in the
 * normal tab order - not an ARIA menu. The trigger is styled as an inverted
 * NavItem (xl size) with a rotating chevron-down icon.
 *
 * Like the former menu it closes again on Escape, on a click outside and
 * once one of its items has been chosen.
 *
 * Once expanded, it scrolls just far enough to show the whole group when the
 * panel opened below the fold.
 */
export const Collapsible = ({ id, label, children }: CollapsibleProps) => {
  const isMobile = useIsMobile();
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <Disclosure
      as="div"
      data-testid={`collapsible-${id}`}
      className="inline-block w-full text-left"
      ref={rootRef}
    >
      {({ open, close }) => (
        <>
          <CloseOnDismiss close={close} open={open} rootRef={rootRef} />
          <Disclosure.Button
            className={clsx(
              "group relative flex items-center gap-2 rounded-md p-1",
              "text-body-lg font-semibold text-foreground-primary-inverse",
              "hover:bg-surface-secondary-inverse active:bg-surface-secondary-inverse",
              "focus:outline-none",
            )}
            tabIndex={KEYBOARD_TAB_INDEX}
          >
            <span className="whitespace-nowrap font-semibold">{label}</span>
            <ChevronDown
              className={clsx(
                "shrink-0 transition ease-linear",
                open && "rotate-180",
              )}
            />
            <FocusRing inverted />
          </Disclosure.Button>
          <Transition
            as={Fragment}
            show={open}
            enter="transition-[max-height,opacity] ease-out duration-300"
            enterFrom="max-h-0 opacity-0"
            enterTo="max-h-96 opacity-100"
            leave="transition-[max-height,opacity] ease-in duration-200"
            leaveFrom="max-h-96 opacity-100"
            leaveTo="max-h-0 opacity-0"
            afterEnter={() =>
              rootRef.current?.scrollIntoView?.({
                behavior: "smooth",
                block: "nearest",
              })
            }
          >
            <Disclosure.Panel
              id={`collapsible-menu-${id}`}
              className={clsx(
                "mt-1 flex flex-col gap-1 focus:outline-none",
                isMobile && "w-screen",
              )}
              static
              onClick={(event: MouseEvent<HTMLElement>) => {
                if ((event.target as Element).closest("a, button")) {
                  close();
                }
              }}
            >
              {children}
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};

type CloseOnDismissProps = {
  close: (focusableElement?: HTMLElement) => void;
  open: boolean;
  rootRef: RefObject<HTMLDivElement | null>;
};

const CloseOnDismiss = ({ close, open, rootRef }: CloseOnDismissProps) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      const trigger = rootRef.current?.querySelector("button") ?? undefined;
      close(trigger);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [close, open, rootRef]);

  return null;
};
